// Utilidades compartidas del módulo Análisis Financiero
import { useLang } from "@/i18n";

export const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export const money = (n, dec = 0) =>
  `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

export const pct = (n) => `${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 1 })}%`;

export const num = (n, dec = 1) =>
  Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: dec });

// Semáforo EBITDA: <25% rojo · 25-32% amarillo · >32% verde.
// Labels visuales se resuelven vía i18n en el SemaforoChip. El SEMAFORO map
// se conserva por retrocompatibilidad con componentes legacy que leen el
// label bruto — nuevos usos deben preferir <SemaforoChip/>.
export const SEMAFORO = {
  rojo:     { key: "semaforoRed",     label: "ALERTA ROJA",  chip: "bg-red-100 text-red-800 border-red-300",             dot: "bg-red-500" },
  amarillo: { key: "semaforoAmber",   label: "PRECAUCIÓN",   chip: "bg-amber-100 text-amber-800 border-amber-300",       dot: "bg-amber-500" },
  ambar:    { key: "semaforoAmber",   label: "ALERTA ÁMBAR", chip: "bg-amber-100 text-amber-800 border-amber-300",       dot: "bg-amber-500" },
  verde:    { key: "semaforoHealthy", label: "SALUDABLE",    chip: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "bg-emerald-500" },
  none:     { key: "noData",          label: "SIN DATOS",    chip: "bg-slate-100 text-slate-500 border-slate-200",       dot: "bg-slate-300" },
};

export function SemaforoChip({ value, testid }) {
  const { t } = useLang();
  const s = SEMAFORO[value] || SEMAFORO.none;
  // Prefer localized label when the i18n key exists, otherwise fall back to
  // the raw ES label so existing screens keep rendering during translation.
  const localized = s.key ? t(s.key) : s.label;
  const label = (localized && localized !== s.key) ? localized : s.label;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest ${s.chip}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {label}
    </span>
  );
}

export function VarBadge({ diff, pct: p, invert = false }) {
  const good = invert ? diff <= 0 : diff >= 0;
  if (diff === 0) return <span className="text-slate-400 text-[11px]">—</span>;
  return (
    <span className={`text-[11px] font-bold ${good ? "text-emerald-700" : "text-red-700"}`}>
      {diff > 0 ? "+" : ""}{money(diff)} ({p > 0 ? "+" : ""}{num(p)}%)
    </span>
  );
}

// Filas del Estado de Resultados: [key, label, tipo]
// tipo: "money" | "pct" | "num" · bold marca subtotales
export const PL_ROWS = [
  { k: "ml_total", l: "ML Ejecutados", t: "num", section: "PRODUCCIÓN" },
  { k: "precio_ajustado_prom", l: "Precio ML ajustado", t: "money2" },
  { k: "ingreso_ml", l: "Ingreso Desarrollo (ML × Precio)", t: "money", section: "ESTADO DE RESULTADOS" },
  { k: "ingresos_adicionales", l: "+ Ingresos adicionales", t: "money" },
  { k: "bonos", l: "+ Bonos cumplimiento", t: "money" },
  { k: "penalizaciones", l: "− Penalizaciones", t: "money", neg: true },
  { k: "ingreso_total", l: "= INGRESO TOTAL", t: "money", bold: true },
  { k: "mano_obra", l: "− Mano de obra (base+IMSS+destajo)", t: "money", neg: true },
  { k: "explosivos", l: "− Explosivos", t: "money", neg: true },
  { k: "aceros", l: "− Aceros de perforación", t: "money", neg: true },
  { k: "diesel", l: "− Diésel", t: "money", neg: true },
  { k: "mantenimiento", l: "− Mantenimiento", t: "money", neg: true },
  { k: "otros_directos", l: "− Otros directos", t: "money", neg: true },
  { k: "costos_directos", l: "= COSTO DIRECTO TOTAL", t: "money", bold: true },
  { k: "utilidad_bruta", l: "= UTILIDAD BRUTA", t: "money", bold: true },
  { k: "margen_bruto_pct", l: "Margen bruto %", t: "pct" },
  { k: "indirectos_obra", l: "− Indirectos de obra", t: "money", neg: true },
  { k: "administracion", l: "− Administración", t: "money", neg: true },
  { k: "ebitda", l: "= EBITDA", t: "money", bold: true, highlight: true },
  { k: "margen_ebitda_pct", l: "Margen EBITDA %", t: "pct", highlight: true },
  { k: "depreciacion", l: "− Depreciación", t: "money", neg: true },
  { k: "ebit", l: "= EBIT", t: "money", bold: true },
  { k: "intereses", l: "− Intereses (crédito equipos)", t: "money", neg: true },
  { k: "ebt", l: "= EBT", t: "money", bold: true },
  { k: "isr", l: "− ISR", t: "money", neg: true },
  { k: "ptu", l: "− PTU", t: "money", neg: true },
  { k: "utilidad_neta", l: "= UTILIDAD NETA", t: "money", bold: true, highlight: true },
  { k: "margen_neto_pct", l: "Margen neto %", t: "pct" },
  { k: "costo_por_ml", l: "Costo total $/ML", t: "money2", section: "COSTO POR ML" },
  { k: "cv_por_ml", l: "Costo variable $/ML", t: "money2" },
  { k: "break_even_ml", l: "Break-even (ML)", t: "num" },
  { k: "fco", l: "Flujo operativo (FCO)", t: "money", section: "FLUJO DE CAJA" },
  { k: "capex", l: "− CAPEX", t: "money", neg: true },
  { k: "fcf", l: "= FLUJO LIBRE (FCF)", t: "money", bold: true },
  { k: "servicio_deuda", l: "− Servicio de deuda", t: "money", neg: true },
  { k: "fcfe", l: "= FREE CASH FLOW AL DUEÑO", t: "money", bold: true, highlight: true },
  { k: "capital_trabajo", l: "Capital de trabajo", t: "money", section: "BALANCE OPERATIVO" },
  { k: "cxc", l: "Cuentas por cobrar", t: "money" },
  { k: "inventario", l: "Inventario en mina", t: "money" },
  { k: "cxp", l: "Cuentas por pagar", t: "money" },
];

export const fmtCell = (row, v) => {
  if (v === undefined || v === null) return "—";
  if (row.t === "pct") return pct(v);
  if (row.t === "num") return num(v);
  if (row.t === "money2") return money(v, 2);
  return money(v);
};
